import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from '@/i18n/LanguageContext';

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export type ShortcutSection =
  | 'workflow'
  | 'selection'
  | 'search'
  | 'savedSearches'
  | 'probeTrail'
  | 'help';

export interface Shortcut {
  keys: string[];
  label: string;
  section: ShortcutSection;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const mod = isMac ? '⌘' : 'Ctrl';

/**
 * All shortcuts the user can trigger. Grouped by `section` so the
 * dialog stays scannable as the keyboard surface grows.
 *
 * Exported so unit tests + the Tour overlay (#55) can introspect
 * the same source of truth as the dialog.
 */
export const SHORTCUTS: Shortcut[] = [
  // Workflow editing
  { section: 'workflow', keys: [mod, 'S'], label: 'Save workflow' },
  { section: 'workflow', keys: [mod, 'Z'], label: 'Undo' },
  { section: 'workflow', keys: [mod, 'Shift', 'Z'], label: 'Redo' },
  { section: 'workflow', keys: [mod, 'Y'], label: 'Redo (alt)' },
  { section: 'workflow', keys: ['Delete'], label: 'Delete selected block' },
  { section: 'workflow', keys: ['Esc'], label: 'Close dialog / cancel gesture' },

  // Selection + canvas navigation
  { section: 'selection', keys: ['Shift', 'Click'], label: 'Add to multi-select' },
  { section: 'selection', keys: ['Double-click layer'], label: 'Enter layer' },
  { section: 'selection', keys: ['Arrow keys'], label: 'Pan viewport in focused minimap' },
  { section: 'selection', keys: ['Space'], label: 'Recenter in focused minimap' },

  // Global search (PR #119) — documented in PR-L (#54)
  { section: 'search', keys: [mod, 'K'], label: 'Focus search bar' },
  { section: 'search', keys: ['/'], label: 'Focus search bar (alt)' },
  { section: 'search', keys: [mod, 'G'], label: 'Next search match' },
  { section: 'search', keys: [mod, 'Shift', 'G'], label: 'Previous search match' },

  // Saved searches — pin/unpin via UI button, surfaced here so
  // reviewers know the capability exists. New net keybindings are a
  // follow-up (would need a SavedSearchesContext keyboard layer).
  {
    section: 'savedSearches',
    keys: ['Pin/Unpin button'],
    label: 'Pin a search query for later (UI-button trigger)',
  },

  // Probe trail (Search Canvas detail panel) — UI-button driven.
  {
    section: 'probeTrail',
    keys: ['Save trail'],
    label: 'Save the current probe trail (UI-button trigger)',
  },
  {
    section: 'probeTrail',
    keys: ['Load trail'],
    label: 'Load a previously-saved trail (UI-button trigger)',
  },

  // Help
  { section: 'help', keys: ['?'], label: 'Show this cheatsheet' },
];

const SECTION_ORDER: ShortcutSection[] = [
  'workflow',
  'selection',
  'search',
  'savedSearches',
  'probeTrail',
  'help',
];

const SECTION_LABEL: Record<ShortcutSection, string> = {
  workflow: 'Workflow editing',
  selection: 'Selection & canvas',
  search: 'Search Canvas',
  savedSearches: 'Saved searches',
  probeTrail: 'Probe trail',
  help: 'Help',
};

function KeyCap({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="px-2 py-0.5 text-xs font-mono rounded border bg-muted text-foreground shadow-sm">
      {children}
    </kbd>
  );
}

export function KeyboardShortcutsDialog({ open, onOpenChange }: KeyboardShortcutsDialogProps) {
  const { t } = useTranslation();
  const grouped: Partial<Record<ShortcutSection, Shortcut[]>> = {};
  for (const s of SHORTCUTS) {
    if (!grouped[s.section]) grouped[s.section] = [];
    grouped[s.section]!.push(s);
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('keyboardShortcutsTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 mt-2">
          {SECTION_ORDER.map((section) => {
            const items = grouped[section];
            if (!items || items.length === 0) return null;
            return (
              <section key={section} aria-label={SECTION_LABEL[section]}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  {SECTION_LABEL[section]}
                </h3>
                <ul className="flex flex-col gap-2">
                  {items.map(({ keys, label }) => (
                    <li
                      key={`${section}-${label}`}
                      className="flex items-center justify-between gap-4 text-sm"
                    >
                      <span className="text-muted-foreground">{label}</span>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        {keys.map((k, i) => (
                          <span key={i} className="flex items-center gap-1">
                            {i > 0 && <span className="text-muted-foreground">+</span>}
                            <KeyCap>{k}</KeyCap>
                          </span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-4">{t('keyboardShortcutsHint')}</p>
      </DialogContent>
    </Dialog>
  );
}
